/* eslint-disable max-lines */
import {
  type ConfirmAttendanceRequest,
  type ShiftId,
} from "@clipboard-health/contract-backend-main";
import {
  BottomSheet,
  Text,
  TextArea,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { DialogContent, Stack, ThemeProvider, ToggleButton } from "@mui/material";
import sendbirdSelectors from "@sendbird/uikit-react/sendbirdSelectors";
// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line import/no-named-as-default
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { BACKEND_MAIN_ERROR_CODES } from "@src/appV2/api/code";
import { isProductionNodeEnvironment } from "@src/appV2/environment";
import { isCbhApiAxiosError } from "@src/appV2/Errors/errorUtils";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { getArrivalTimeAwareMaxEtaIncrement } from "@src/appV2/redesign/ArrivalTime/getArrivalTimeAwareMaxEtaIncrement";
import { Button } from "@src/appV2/redesign/components/Button";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import {
  UPDATE_ETA_BOTTOM_SHEET_SOURCE,
  type UpdateEtaBottomSheetSource,
} from "@src/appV2/redesign/Shift/WorkerEta/UpdateEta/types";
import { areTimeDisplaysIdenticalAcrossTimezones } from "@src/appV2/redesign/utils/areTimeDisplaysIdenticalAcrossTimezones";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";
import { getTimezoneAbbreviation } from "@src/appV2/redesign/utils/getTimezoneAbbreviation";
import { useScrollToViewOnKeyboard } from "@src/appV2/redesign/utils/useScrollToViewOnKeyboard";
import { useConfirmAttendance } from "@src/appV2/Shifts/UrgentShifts/api/useConfirmAttendance";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import {
  addMinutes,
  differenceInMinutes,
  formatISO,
  parseISO,
  roundToNearestMinutes,
} from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { useMemo, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { z } from "zod";

import { useGetShiftDetailsData } from "../api/useGetShiftDetailsData";
import { hasShiftStarted } from "../utils/hasShiftStarted";

interface UpdateEtaBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  source?: UpdateEtaBottomSheetSource;
}

/**
 * @deprecated Use the UpdateEtaBottomSheetContainer instead.
 */
export function UpdateEtaBottomSheet(props: UpdateEtaBottomSheetProps) {
  const { modalState, shiftId, source: sourceProperty } = props;

  const { source: sourceQueryParameter } = useSearchParams<{
    source?: UpdateEtaBottomSheetSource;
  }>();

  const source = sourceQueryParameter ?? sourceProperty ?? UPDATE_ETA_BOTTOM_SHEET_SOURCE.DEEP_LINK;

  const currentDate = useCurrentDate(1000);
  const { shift, isLoading } = useGetShiftDetailsData({ shiftId });

  if (!isDefined(shift) || isLoading) {
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <UpdateEtaBottomSheetContent
        modalState={modalState}
        shiftStartDate={parseISO(shift.start)}
        currentDate={currentDate}
        shiftId={shift._id}
        workplaceId={shift.facilityId}
        workplaceTimezone={shift.facility.tmz ?? ""}
        shiftName={shift.name}
        targetArrivalTime={
          isDefined(shift.computedArrival?.targetArrivalTime)
            ? parseISO(shift.computedArrival.targetArrivalTime)
            : undefined
        }
        source={source}
      />
    </ThemeProvider>
  );
}

interface UpdateEtaBottomSheetContentProps {
  modalState: UseModalState;
  shiftStartDate: Date;
  currentDate: Date;
  shiftId: string;
  workplaceId: string;
  workplaceTimezone: string;
  shiftName: string;
  targetArrivalTime?: Date;
  source: UpdateEtaBottomSheetSource;
}

const updateEtaSchema = z.object({
  arrivalTime: z.string().min(1, "Please select an arrival time"),
  message: z.string().optional(),
});

type UpdateEtaFormValues = z.infer<typeof updateEtaSchema>;

function UpdateEtaBottomSheetContent(props: UpdateEtaBottomSheetContentProps) {
  const {
    modalState,
    shiftStartDate,
    currentDate,
    shiftId,
    workplaceId,
    workplaceTimezone,
    shiftName,
    targetArrivalTime,
    source,
  } = props;

  const history = useHistory();
  const { showSuccessToast, showErrorToast, showWarningToast } = useToast();
  const sendBirdStateContext = useSendbirdStateContext();

  const worker = useDefinedWorker();
  const workerTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { mutateAsync: onConfirmAttendance } = useConfirmAttendance();

  const inputRef = useRef<HTMLTextAreaElement>(null);
  useScrollToViewOnKeyboard(inputRef);

  const arrivalTimeOptions = useMemo(() => {
    let maxIncrement = getArrivalTimeAwareMaxEtaIncrement({
      targetArrivalTime,
      shiftStartDate,
      defaultMaxIncrement: 60,
    });

    // Round maxIncrement up to the nearest 10
    maxIncrement = Math.ceil(maxIncrement / 10) * 10;

    // Generate time increments from 10 to maxIncrement in steps of 10
    const timeIncrements: number[] = [];
    for (let minutes = 10; minutes <= maxIncrement; minutes += 10) {
      timeIncrements.push(minutes);
    }

    // If current time is after shift start time, round to the nearest 10 minute interval
    // Otherwise, use the shift start time as the base time
    const baseTime: Date = hasShiftStarted({
      shiftStartDate,
      currentDate,
    })
      ? roundToNearestMinutes(currentDate, { nearestTo: 10 })
      : shiftStartDate;

    return timeIncrements.map((minutes) => {
      const time = addMinutes(baseTime, minutes);
      return {
        value: formatISO(time),
        displayTime: formatTimeWithTimezone(time, workplaceTimezone, false),
      };
    });
  }, [currentDate, shiftStartDate, workplaceTimezone, targetArrivalTime]);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<UpdateEtaFormValues>({
    resolver: zodResolver(updateEtaSchema),
    defaultValues: {
      arrivalTime: arrivalTimeOptions.length > 0 ? arrivalTimeOptions[0].value : "",
      message: "",
    },
  });

  const selectedTime = watch("arrivalTime");

  function showEtaUpdateSuccessToast(params: {
    shiftStart: string;
    workerTimezone: string;
    shiftName: string;
  }) {
    const { shiftStart, workerTimezone, shiftName } = params;
    showSuccessToast(
      `You successfully updated your ETA for your ${formatInTimeZone(
        parseISO(shiftStart),
        workerTimezone,
        "MM/dd"
      )} ${shiftName.toUpperCase()} shift`
    );
  }

  async function onConfirmAttendanceSuccess(params: {
    payload: ConfirmAttendanceRequest;
    workplaceId: string;
    messageToFacility: string;
    source: UpdateEtaBottomSheetSource;
  }) {
    const { payload, messageToFacility } = params;
    const sdk = sendBirdStateContext?.stores?.sdkStore?.sdk;
    const sendUserMessage = sendbirdSelectors.getSendUserMessage(sendBirdStateContext);

    if (isDefined(workplaceId) && isDefined(sdk) && isDefined(sdk.groupChannel)) {
      const channel = await sdk.groupChannel.getChannel(`${workplaceId}_${worker.userId}`);
      sendUserMessage(channel, { message: messageToFacility });
    } else {
      logError("Failed to send message to facility via Sendbird", {
        error: "Sendbird is not available",
      });
    }

    if (isDefined(workplaceId)) {
      showEtaUpdateSuccessToast({
        shiftStart: shiftStartDate.toISOString(),
        workerTimezone,
        shiftName,
      });

      history.push(`/home/account/chat/${workplaceId}`);
    } else if (!isProductionNodeEnvironment()) {
      const toastWarning = `Our sendbird provider is configured to only connect on pages that start with /chat.
 To test dev/ locally update the logic in SendbirdProvider to define appId`;
      showWarningToast(toastWarning);
    }

    logEvent(APP_V2_USER_EVENTS.SUBMITTED_ETA_MODAL, {
      shiftId: payload.data.relationships.shift.data.id,
      workerId: worker.userId,
      facilityId: workplaceId,
      shiftStart: shiftStartDate.toISOString(),
      createdAt: new Date().toISOString(),
      etaSubmitted: payload.data.attributes.etaAt,
      messageToFacility,
      source,
      version: "v1",
    });

    modalState.closeModal();
  }

  const onSubmit = handleSubmit(async (data: UpdateEtaFormValues) => {
    const selectedTimeOption = arrivalTimeOptions.find(
      (option) => option.value === data.arrivalTime
    );

    if (!selectedTimeOption) {
      showErrorToast("Please select an arrival time");
      return;
    }

    const messageToFacility = formatMessageToFacility({
      shiftStartDate,
      workplaceTimezone,
      selectedTime: data.arrivalTime,
      message: data.message,
    });

    const payload: ConfirmAttendanceRequest = {
      data: {
        type: "attendance-confirmation",
        attributes: {
          etaAt: selectedTimeOption.value,
        },
        relationships: {
          shift: {
            data: {
              type: "shift",
              id: shiftId as ShiftId,
            },
          },
        },
      },
    };

    try {
      await onConfirmAttendance(payload);
      await onConfirmAttendanceSuccess({ payload, workplaceId, messageToFacility, source });
    } catch (error) {
      if (isCbhApiAxiosError(error)) {
        const isShiftNoLongerAvailable = error.response.data.errors.some(
          (apiError) => apiError.code === BACKEND_MAIN_ERROR_CODES.shiftAssignedToAnotherWorker
        );

        if (isShiftNoLongerAvailable) {
          showErrorToast("This shift is no longer available");
          modalState.closeModal();
          history.replace("/home-v2/shift-discovery/list");
          return;
        }
      }

      showErrorToast("Something went wrong while updating your ETA. Please try again.");

      logError(APP_V2_APP_EVENTS.UPDATE_YOUR_ETA_FAILURE, {
        error,
        metadata: {
          shiftId,
          workplaceId,
        },
      });
    }
  });

  return (
    <BottomSheet modalState={modalState}>
      <DialogContent>
        <Stack spacing={8} sx={{ paddingTop: 8 }}>
          <Stack spacing={4}>
            <Title semibold variant="h3" component="h3">
              When will you arrive?
            </Title>
            <Text variant="body2">Let the workplace know when you will arrive.</Text>
          </Stack>

          <Stack spacing={6}>
            <Text semibold>
              {areTimeDisplaysIdenticalAcrossTimezones(
                shiftStartDate,
                workplaceTimezone,
                workerTimezone
              )
                ? "I will arrive at"
                : `I will arrive at (${getTimezoneAbbreviation(workplaceTimezone)})`}
            </Text>
            <Controller
              name="arrivalTime"
              control={control}
              render={({ field }) => (
                <Stack direction="row" flexWrap="wrap" sx={{ gap: 4 }}>
                  {arrivalTimeOptions.map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      sx={{ minWidth: "auto", px: 2, flex: "1 0 30%" }}
                      selected={field.value === option.value}
                      data-testid="time-option"
                      onChange={(_, value) => {
                        if (value !== null) {
                          field.onChange(value);
                        }
                      }}
                    >
                      {option.displayTime}
                    </ToggleButton>
                  ))}
                </Stack>
              )}
            />
          </Stack>

          <Stack spacing={4}>
            <Text semibold>Your message</Text>
            <Text sx={{ fontStyle: "italic" }}>
              {formatMessageToFacility({
                shiftStartDate,
                workplaceTimezone,
                selectedTime,
              })}
            </Text>
          </Stack>

          <Stack spacing={4}>
            <Text semibold>Additional message (optional)</Text>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <TextArea
                  fullWidth
                  rows={3}
                  placeholder="Provide additional details"
                  inputRef={inputRef}
                  {...field}
                />
              )}
            />
          </Stack>

          <Stack direction="column" gap={4} alignItems="center">
            <Button
              fullWidth
              size="large"
              variant="contained"
              disabled={isSubmitting}
              endIconType="plane"
              onClick={onSubmit}
            >
              Send message
            </Button>
            <IconButton
              aria-label="Close Full Screen Dialog Button"
              size="large"
              variant="outlined"
              iconType="close"
              onClick={(event) => {
                event.stopPropagation();
                modalState.closeModal();
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}

interface MessageToFacilityParams {
  shiftStartDate: Date;
  workplaceTimezone: string;
  selectedTime: string;
  message?: string;
}

function formatMessageToFacility(params: MessageToFacilityParams) {
  const { shiftStartDate, workplaceTimezone, selectedTime, message } = params;

  const shiftTime = formatTimeWithTimezone(shiftStartDate, workplaceTimezone, false);
  const arrivalTime = formatTimeWithTimezone(parseISO(selectedTime), workplaceTimezone, true);
  const isOnTime = Math.abs(differenceInMinutes(parseISO(selectedTime), shiftStartDate)) <= 1;

  const additionalMessage = message ? ` ${message}` : "";

  if (isOnTime) {
    return `Hi, I am going to be on time. I will arrive by ${arrivalTime}.${additionalMessage}`;
  }

  return `Hi, I'm running late to my ${shiftTime} shift. I will arrive by ${arrivalTime}.${additionalMessage}`;
}

/* eslint-enable max-lines */
